<script setup lang="ts">
import MyFocusGoalCard from './MyFocusGoalCard.vue';
import type { GoalItem } from './types';

const props = defineProps<{
	loading: boolean;
	goalsInProgress: GoalItem[];
	goalsCompleted: GoalItem[];
	hasCompleted: boolean;
	showPastGoals: boolean;
	getTitle: (goal: GoalItem) => string;
	getSubtitle: (goal: GoalItem) => string;
	getPercentage: (goal: GoalItem) => number;
	getAccentColor: (goal: GoalItem) => string;
	formatDate: (iso: string | null | undefined) => string;
}>();

const emit = defineEmits<{
	'toggle-past-goals': [];
}>();
</script>

<template>
	<section class="mb-8">
		<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
			I TUOI OBIETTIVI
		</h2>
		<div v-if="loading" class="flex items-center justify-center py-8 text-gray-500">
			<span class="material-symbols-outlined animate-spin text-3xl">progress_activity</span>
		</div>
		<div
			v-else-if="goalsInProgress.length === 0 && !hasCompleted"
			class="bg-white p-6 text-center text-gray-500 text-sm"
		>
			Nessun obiettivo per questo percorso al momento.
		</div>
		<template v-else>
			<div
				v-if="goalsInProgress.length === 0 && hasCompleted"
				class="bg-white p-4 text-center text-gray-500 text-sm mb-2"
			>
				Nessun obiettivo in corso per questo percorso.
			</div>
			<div v-if="goalsInProgress.length" class="grid gap-4 sm:grid-cols-2">
				<MyFocusGoalCard
					v-for="goal in goalsInProgress"
					:key="goal.id"
					:goal="goal"
					:title="getTitle(goal)"
					:subtitle="getSubtitle(goal)"
					:percentage="getPercentage(goal)"
					:accent-color="getAccentColor(goal)"
					:expected-date-formatted="formatDate(goal.expected_completion_date)"
				/>
			</div>
			<button
				v-if="hasCompleted"
				type="button"
				class="mt-4 flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2"
				:aria-expanded="showPastGoals"
				@click="emit('toggle-past-goals')"
			>
				<span
					class="material-symbols-outlined text-lg transition-transform"
					:class="{ 'rotate-180': showPastGoals }"
				>
					expand_more
				</span>
				{{ showPastGoals ? 'Nascondi obiettivi passati' : 'Visualizza obiettivi passati' }}
			</button>
			<div
				v-show="hasCompleted"
				class="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
				:style="{
					maxHeight: showPastGoals ? '2000px' : '0',
					opacity: showPastGoals ? 1 : 0,
				}"
			>
				<template v-if="goalsCompleted.length">
					<h3 class="mt-6 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
						Obiettivi raggiunti
					</h3>
					<div class="grid gap-4 sm:grid-cols-2">
						<MyFocusGoalCard
							v-for="goal in goalsCompleted"
							:key="goal.id"
							:goal="goal"
							:title="getTitle(goal)"
							:subtitle="getSubtitle(goal)"
							:percentage="getPercentage(goal)"
							:accent-color="getAccentColor(goal)"
							:expected-date-formatted="formatDate(goal.expected_completion_date)"
						/>
					</div>
				</template>
			</div>
		</template>
	</section>
</template>
